/**
 * Signal Variant Auto-Selector
 * Product: Superveloce / Rush - U.S. Fender Eliminator
 * Template: product.us-sv-tail-tidy
 *
 * The customer chooses Bike Model + License Bracket as line-item
 * properties, while Shopify inventory is controlled by a hidden
 * "Signal" variant option:
 *
 *   no bottom mount: "SV800 Tucked"
 *     - any bike model + None
 *     - Superveloce 800 + Tucked Bracket
 *
 *   bottom mount: "Standard"
 *     - Superveloce 800 + Regular Bracket
 *     - Rush/Superveloce 1000 + Regular or Tucked Bracket
 */

(function injectHideStyle() {
  if (document.getElementById('signal-variant-hide')) return;

  const style = document.createElement('style');
  style.id = 'signal-variant-hide';
  style.textContent = '[data-option-name="Signal"] { display: none !important; visibility: hidden !important; }';
  document.head.appendChild(style);
})();

class SignalVariantSelector {
  constructor() {
    this.signalOptionName = 'Signal';
    this.standardSignalValue = 'Standard';
    this.tuckedSignalValue = 'SV800 Tucked';
    this.bikeModelSelect = null;
    this.licenseBracketSelect = null;
    this.variantPicker = null;
    this.initialized = false;
    this.isSelectingSignal = false;
    this.observer = null;

    // Addon dropdowns can render after this deferred script has executed.
    this.trySetup();
    document.addEventListener('DOMContentLoaded', () => this.trySetup());
    setTimeout(() => this.trySetup(), 250);
    setTimeout(() => this.trySetup(), 1000);
  }

  trySetup() {
    if (this.initialized) return;

    this.variantPicker = document.querySelector('variant-radios, variant-selects');
    const signalFieldset = this.getSignalFieldset();
    if (!this.variantPicker || !signalFieldset) return;

    this.hideSignalFieldset(signalFieldset);
    this.bikeModelSelect = this.findAddonSelect('Bike Model');
    this.licenseBracketSelect = this.findAddonSelect('License Bracket');

    if (!this.bikeModelSelect || !this.licenseBracketSelect) return;

    this.bikeModelSelect.addEventListener('change', () => this.scheduleUpdate());
    this.licenseBracketSelect.addEventListener('change', () => this.scheduleUpdate());
    document.addEventListener('addon:changed', () => this.scheduleUpdate());

    this.variantPicker.addEventListener('change', (event) => {
      if (this.isSignalInput(event.target)) return;

      this.scheduleUpdate();
      this.scheduleUpdate(300);
    });

    document.addEventListener('submit', (event) => {
      if (event.target.matches('form[action*="/cart/add"]')) {
        this.update({ syncOnly: true });
      }
    }, true);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.productVariantChange, () => this.scheduleUpdate());
    }

    this.observeVariantPicker();
    this.initialized = true;
    this.update();
  }

  findAddonSelect(label) {
    const targetLabel = this.normalizeText(label);
    const addonDropdowns = document.querySelectorAll('.product-addon-dropdown');

    return Array.from(addonDropdowns).find((dropdown) => {
      return this.normalizeText(dropdown.dataset.addonLabel || '') === targetLabel;
    })?.querySelector('[data-addon-select]') || null;
  }

  observeVariantPicker() {
    if (this.observer || !this.variantPicker) return;

    this.observer = new MutationObserver(() => {
      const signalFieldset = this.getSignalFieldset();
      if (signalFieldset) {
        this.hideSignalFieldset(signalFieldset);
        this.scheduleUpdate();
      }
    });

    this.observer.observe(this.variantPicker, {
      attributes: true,
      childList: true,
      subtree: true,
    });
  }

  scheduleUpdate(delay = 0) {
    window.setTimeout(() => this.update(), delay);
  }

  update(options = {}) {
    const targetSignal = this.getTargetSignal();
    if (!targetSignal) return;

    if (options.syncOnly) {
      this.syncProductFormVariant(targetSignal);
      return;
    }

    this.selectSignal(targetSignal);
  }

  getTargetSignal() {
    const bike = this.getSelectedAddon(this.bikeModelSelect);
    const bracket = this.getSelectedAddon(this.licenseBracketSelect);

    if (!bike.value || bike.type === 'empty' || !bracket.value || bracket.type === 'empty') {
      return null;
    }

    const bikeKey = this.normalizeKey(bike.label);
    const bracketKey = this.normalizeKey(bracket.label);
    const bracketText = this.normalizeText(bracket.label);

    const isSuperveloce800 = bikeKey.includes('superveloce800');
    const isNoBracket = bracketKey === 'none' || bracket.value === 'text-1';
    const isTuckedBracket = bracketText.includes('tucked');
    const needsNoBottomMount = isNoBracket || (isSuperveloce800 && isTuckedBracket);

    return needsNoBottomMount ? this.tuckedSignalValue : this.standardSignalValue;
  }

  getSelectedAddon(select) {
    const selectedOption = select?.options[select.selectedIndex];

    return {
      value: select?.value || '',
      type: selectedOption?.dataset.type || '',
      label: this.getOptionLabel(selectedOption),
    };
  }

  getOptionLabel(option) {
    if (!option) return '';

    const label =
      option.dataset.text ||
      option.dataset.productTitle ||
      option.textContent ||
      '';

    return label.replace(/\s+-\s+\$[\d,.]+$/, '').trim();
  }

  selectSignal(value) {
    const signalFieldset = this.getSignalFieldset();
    if (!signalFieldset) return;

    this.hideSignalFieldset(signalFieldset);

    const radio = Array.from(signalFieldset.querySelectorAll('input[type="radio"]'))
      .find((input) => input.value === value);

    if (!radio) {
      const availableValues = Array.from(signalFieldset.querySelectorAll('input[type="radio"]'))
        .map((input) => input.value)
        .join(', ');
      console.warn(`[SignalVariantSelector] Could not find "${value}" signal variant. Available: ${availableValues}`);
      return;
    }

    if (!radio.checked) {
      this.isSelectingSignal = true;
      signalFieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false;
        input.classList.remove('checked');
      });

      radio.checked = true;
      radio.classList.add('checked');
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      this.isSelectingSignal = false;
    }

    this.syncProductFormVariant(value);
  }

  syncProductFormVariant(signalValue) {
    const targetVariant = this.findVariantForSignal(signalValue);
    if (!targetVariant) return false;

    const productForms = [
      document.getElementById(`product-form-${this.variantPicker.dataset.section}`),
      document.getElementById('product-form-installment'),
    ].filter(Boolean);

    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      if (!input || input.value === String(targetVariant.id)) return;

      input.value = targetVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return true;
  }

  findVariantForSignal(signalValue) {
    const variants = this.getVariantData();
    if (!variants.length) return null;

    const selectedOptions = this.getSelectedVariantOptions(signalValue);
    if (!selectedOptions.length || selectedOptions.some((value) => value === null)) return null;

    return variants.find((variant) => {
      return variant.options.every((optionValue, index) => optionValue === selectedOptions[index]);
    }) || null;
  }

  getSelectedVariantOptions(signalValue) {
    if (!this.variantPicker) return [];

    return Array.from(this.variantPicker.querySelectorAll('fieldset[data-option-position], select[data-option-position]'))
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((optionGroup) => {
        const optionName = this.getVariantOptionName(optionGroup);
        if (this.normalizeText(optionName) === this.normalizeText(this.signalOptionName)) {
          return signalValue;
        }

        if (optionGroup.matches('select')) {
          return optionGroup.selectedOptions[0]?.value || null;
        }

        return optionGroup.querySelector('input[type="radio"]:checked')?.value || null;
      });
  }

  getVariantOptionName(optionGroup) {
    if (optionGroup.dataset.optionName) return optionGroup.dataset.optionName;

    const position = Number(optionGroup.dataset.optionPosition);
    const optionsWithValues = this.getOptionsWithValues();
    return optionsWithValues.find((option) => option.position === position)?.name || '';
  }

  getOptionsWithValues() {
    const script = this.variantPicker?.querySelector('[type="application/json"][data-id="product-options-with-values"]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[SignalVariantSelector] Could not parse product option data.', error);
      return [];
    }
  }

  getVariantData() {
    const script = this.variantPicker?.querySelector('[type="application/json"][data-id="product-variants"]');
    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[SignalVariantSelector] Could not parse product variant data.', error);
      return [];
    }
  }

  getSignalFieldset() {
    const selector = `[data-option-name="${this.signalOptionName}"]`;
    return this.variantPicker?.querySelector(selector) || document.querySelector(selector);
  }

  hideSignalFieldset(fieldset) {
    if (fieldset.style.display !== 'none') {
      fieldset.style.display = 'none';
    }

    if (fieldset.style.visibility !== 'hidden') {
      fieldset.style.visibility = 'hidden';
    }

    if (fieldset.getAttribute('aria-hidden') !== 'true') {
      fieldset.setAttribute('aria-hidden', 'true');
    }
  }

  isSignalInput(input) {
    return Boolean(input?.closest?.(`[data-option-name="${this.signalOptionName}"]`));
  }

  normalizeText(value) {
    return String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
  }

  normalizeKey(value) {
    return this.normalizeText(value).replace(/[^a-z0-9]/g, '');
  }
}

// setup() will bail early if the hidden Signal option is not present.
window.signalVariantSelector = new SignalVariantSelector();
